import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Calendar,
  Plus,
  MapPin,
  Clock,
  Edit,
  Trash2,
  Megaphone,
  AlertCircle,
  Info
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EventsPage = () => {
  const { user, chapter } = useAuth();
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [activeTab, setActiveTab] = useState("events");

  const token = localStorage.getItem("token");
  const primaryColor = chapter?.primary_color || "#CC0000";
  const isAdmin = user?.role === "admin";

  const [eventForm, setEventForm] = useState({
    title: "", description: "", event_date: "", event_time: "", location: "", event_type: "chapter"
  });
  const [annForm, setAnnForm] = useState({
    title: "", content: "", priority: "normal", active: true
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [evtRes, annRes] = await Promise.all([
        fetch(`${API}/events`, { headers }),
        fetch(`${API}/announcements`, { headers })
      ]);
      if (evtRes.ok) setEvents(await evtRes.json());
      if (annRes.ok) setAnnouncements(await annRes.json());
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title || !eventForm.event_date) {
      toast.error("Title and date are required");
      return;
    }
    try {
      const url = editingEvent ? `${API}/events/${editingEvent.event_id}` : `${API}/events`;
      const method = editingEvent ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(eventForm)
      });
      if (res.ok) {
        toast.success(editingEvent ? "Event updated" : "Event created");
        setEventDialogOpen(false);
        setEditingEvent(null);
        setEventForm({ title: "", description: "", event_date: "", event_time: "", location: "", event_type: "chapter" });
        fetchData();
      } else {
        toast.error("Failed to save event");
      }
    } catch (error) {
      toast.error("Error saving event");
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      const res = await fetch(`${API}/events/${eventId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Event deleted");
        fetchData();
      }
    } catch (error) {
      toast.error("Error deleting event");
    }
  };

  const handleSaveAnnouncement = async (e) => {
    e.preventDefault();
    if (!annForm.title || !annForm.content) {
      toast.error("Title and content are required");
      return;
    }
    try {
      const url = editingAnnouncement ? `${API}/announcements/${editingAnnouncement.announcement_id}` : `${API}/announcements`;
      const method = editingAnnouncement ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(annForm)
      });
      if (res.ok) {
        toast.success(editingAnnouncement ? "Announcement updated" : "Announcement created");
        setAnnouncementDialogOpen(false);
        setEditingAnnouncement(null);
        setAnnForm({ title: "", content: "", priority: "normal", active: true });
        fetchData();
      } else {
        toast.error("Failed to save announcement");
      }
    } catch (error) {
      toast.error("Error saving announcement");
    }
  };

  const handleDeleteAnnouncement = async (annId) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      const res = await fetch(`${API}/announcements/${annId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Announcement deleted");
        fetchData();
      }
    } catch (error) {
      toast.error("Error deleting announcement");
    }
  };

  const openEditEvent = (evt) => {
    setEditingEvent(evt);
    setEventForm({
      title: evt.title, description: evt.description || "", event_date: evt.event_date,
      event_time: evt.event_time || "", location: evt.location || "", event_type: evt.event_type
    });
    setEventDialogOpen(true);
  };

  const openEditAnnouncement = (ann) => {
    setEditingAnnouncement(ann);
    setAnnForm({ title: ann.title, content: ann.content, priority: ann.priority, active: ann.active });
    setAnnouncementDialogOpen(true);
  };

  const priorityColors = { high: "bg-red-100 text-red-700", normal: "bg-blue-100 text-blue-700", low: "bg-slate-100 text-slate-700" };
  const typeColors = { chapter: "bg-blue-100 text-blue-700", regional: "bg-purple-100 text-purple-700", training: "bg-green-100 text-green-700" };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="events-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">Events & Announcements</h1>
            <p className="text-muted-foreground mt-1">Stay updated with chapter activities</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab("events")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "events" ? "border-current" : "border-transparent text-muted-foreground hover:text-slate-700"
            }`}
            style={activeTab === "events" ? { color: primaryColor, borderColor: primaryColor } : {}}
            data-testid="tab-events"
          >
            <Calendar className="h-4 w-4 inline mr-2" />
            Events ({events.length})
          </button>
          <button
            onClick={() => setActiveTab("announcements")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "announcements" ? "border-current" : "border-transparent text-muted-foreground hover:text-slate-700"
            }`}
            style={activeTab === "announcements" ? { color: primaryColor, borderColor: primaryColor } : {}}
            data-testid="tab-announcements"
          >
            <Megaphone className="h-4 w-4 inline mr-2" />
            Announcements ({announcements.length})
          </button>
        </div>

        {/* Events Tab */}
        {activeTab === "events" && (
          <div className="space-y-4">
            {isAdmin && (
              <Dialog open={eventDialogOpen} onOpenChange={(open) => {
                setEventDialogOpen(open);
                if (!open) { setEditingEvent(null); setEventForm({ title: "", description: "", event_date: "", event_time: "", location: "", event_type: "chapter" }); }
              }}>
                <DialogTrigger asChild>
                  <Button style={{ backgroundColor: primaryColor }} data-testid="add-event-btn">
                    <Plus className="mr-2 h-4 w-4" /> Add Event
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingEvent ? "Edit Event" : "Add Event"}</DialogTitle>
                    <DialogDescription>Fill in the event details below</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSaveEvent} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} data-testid="event-title-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date *</Label>
                        <Input type="date" value={eventForm.event_date} onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })} data-testid="event-date-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Time</Label>
                        <Input type="time" value={eventForm.event_time} onChange={(e) => setEventForm({ ...eventForm, event_time: e.target.value })} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Location</Label>
                      <Input value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select value={eventForm.event_type} onValueChange={(v) => setEventForm({ ...eventForm, event_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="chapter">Chapter</SelectItem>
                          <SelectItem value="regional">Regional</SelectItem>
                          <SelectItem value="training">Training</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} rows={3} />
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <Button type="button" variant="outline" onClick={() => setEventDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" style={{ backgroundColor: primaryColor }}>{editingEvent ? "Update" : "Create"}</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {events.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No events scheduled yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {events.map(evt => (
                  <Card key={evt.event_id} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`event-card-${evt.event_id}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={typeColors[evt.event_type]}>{evt.event_type}</Badge>
                          </div>
                          <h3 className="font-semibold text-lg">{evt.title}</h3>
                          {evt.description && <p className="text-sm text-muted-foreground mt-1">{evt.description}</p>}
                          <div className="mt-3 space-y-1">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Calendar className="h-4 w-4" /> {evt.event_date}
                              {evt.event_time && <><Clock className="h-4 w-4 ml-2" /> {evt.event_time}</>}
                            </div>
                            {evt.location && (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <MapPin className="h-4 w-4" /> {evt.location}
                              </div>
                            )}
                          </div>
                        </div>
                        {isAdmin && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEditEvent(evt)} data-testid={`edit-event-${evt.event_id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteEvent(evt.event_id)} data-testid={`delete-event-${evt.event_id}`}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Announcements Tab */}
        {activeTab === "announcements" && (
          <div className="space-y-4">
            {isAdmin && (
              <Dialog open={announcementDialogOpen} onOpenChange={(open) => {
                setAnnouncementDialogOpen(open);
                if (!open) { setEditingAnnouncement(null); setAnnForm({ title: "", content: "", priority: "normal", active: true }); }
              }}>
                <DialogTrigger asChild>
                  <Button style={{ backgroundColor: primaryColor }} data-testid="add-announcement-btn">
                    <Plus className="mr-2 h-4 w-4" /> Add Announcement
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingAnnouncement ? "Edit Announcement" : "Add Announcement"}</DialogTitle>
                    <DialogDescription>Fill in the announcement details below</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSaveAnnouncement} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} data-testid="announcement-title-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Content *</Label>
                      <Textarea value={annForm.content} onChange={(e) => setAnnForm({ ...annForm, content: e.target.value })} rows={4} data-testid="announcement-content-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Select value={annForm.priority} onValueChange={(v) => setAnnForm({ ...annForm, priority: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <Button type="button" variant="outline" onClick={() => setAnnouncementDialogOpen(false)}>Cancel</Button>
                      <Button type="submit" style={{ backgroundColor: primaryColor }}>{editingAnnouncement ? "Update" : "Create"}</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}

            {announcements.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <Megaphone className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No announcements yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {announcements.map(ann => (
                  <Card key={ann.announcement_id} className="border-0 shadow-sm" data-testid={`announcement-card-${ann.announcement_id}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={priorityColors[ann.priority]}>{ann.priority}</Badge>
                            {!ann.active && <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>}
                          </div>
                          <h3 className="font-semibold text-lg">{ann.title}</h3>
                          <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{ann.content}</p>
                          <p className="text-xs text-muted-foreground mt-2">{ann.created_at?.split("T")[0]}</p>
                        </div>
                        {isAdmin && (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEditAnnouncement(ann)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteAnnouncement(ann.announcement_id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default EventsPage;
